/**
 * Internationalization (i18n) Context Provider
 * 
 * Provides global language and theme preferences across the entire portal.
 * Supports: English (en), Spanish (es), French (fr)
 * Theme: light, dark, system
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';
import frTranslations from '../locales/fr.json';
// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');

// Translation dictionaries
const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'];

const translations = {
  en: enTranslations,
  es: esTranslations,
  fr: frTranslations,
};

// Default values
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_THEME = 'system';

// Context
const I18nContext = createContext(null);

/**
 * Get nested translation value by key path
 * @param {object} obj - Translation object
 * @param {string} path - Dot-separated path (e.g., 'settings.title')
 * @returns {string} - Translated string or the key if not found
 */
/**
 * Resolve a dot-path to a string leaf; returns undefined if missing.
 */
const getNestedValue = (obj, path) => {
  if (typeof path !== 'string' || !path) {
    return undefined;
  }
  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return undefined;
    }
  }

  return typeof value === 'string' ? value : undefined;
};

/**
 * I18n Provider Component
 */
export const I18nProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('fundelite_language');
    return SUPPORTED_LANGUAGES.includes(saved) ? saved : DEFAULT_LANGUAGE;
  });
  
  const [theme, setThemeState] = useState(() => {
    const saved = localStorage.getItem('fundelite_theme');
    return saved || DEFAULT_THEME;
  });
  
  const [isLoading, setIsLoading] = useState(true);

  // Get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('fundelite_token');
  }, []);

  // Track token changes to reload preferences after login
  const [tokenVersion, setTokenVersion] = useState(0);
  
  // Listen for token changes in localStorage (triggers on login/logout)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'fundelite_token') {
        setTokenVersion(v => v + 1);
      }
    };
    
    // Also check periodically for token (handles same-tab login)
    const checkToken = () => {
      const token = localStorage.getItem('fundelite_token');
      if (token && tokenVersion === 0) {
        setTokenVersion(1);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(checkToken, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [tokenVersion]);

  // Load preferences from backend on mount and when token changes
  useEffect(() => {
    const loadPreferences = async () => {
      const token = getAuthToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/users/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data) {
          if (response.data.language_preference) {
            const pref = response.data.language_preference;
            const normalized = SUPPORTED_LANGUAGES.includes(pref) ? pref : DEFAULT_LANGUAGE;
            setLanguageState(normalized);
            localStorage.setItem('fundelite_language', normalized);
          }
          if (response.data.theme_preference) {
            setThemeState(response.data.theme_preference);
            localStorage.setItem('fundelite_theme', response.data.theme_preference);
          }
        }
      } catch (error) {
        // Use local preferences on error
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, [getAuthToken, tokenVersion]);

  // Apply theme to document
  useEffect(() => {
    const applyTheme = () => {
      const root = document.documentElement;
      let effectiveTheme = theme;
      
      if (theme === 'system') {
        effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      
      root.classList.remove('light', 'dark');
      root.classList.add(effectiveTheme);
      root.setAttribute('data-theme', effectiveTheme);
      
      // Store effective theme for CSS usage
      document.body.setAttribute('data-effective-theme', effectiveTheme);
    };
    
    applyTheme();
    
    // Listen for system theme changes if using 'system'
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme();
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
  }, [theme]);

  // Set language and persist
  const setLanguage = useCallback(async (newLang) => {
    if (!SUPPORTED_LANGUAGES.includes(newLang)) return;
    
    setLanguageState(newLang);
    localStorage.setItem('fundelite_language', newLang);
    
    // Persist to backend
    const token = getAuthToken();
    if (token) {
      try {
        await axios.put(
          `${BACKEND_URL}/api/users/preferences`,
          { language_preference: newLang },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  }, [getAuthToken]);

  // Set theme and persist
  const setTheme = useCallback(async (newTheme) => {
    if (!['light', 'dark', 'system'].includes(newTheme)) return;
    
    setThemeState(newTheme);
    localStorage.setItem('fundelite_theme', newTheme);
    
    // Persist to backend
    const token = getAuthToken();
    if (token) {
      try {
        await axios.put(
          `${BACKEND_URL}/api/users/preferences`,
          { theme_preference: newTheme },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  }, [getAuthToken]);

  // Translation function
  const t = useCallback((key, params = null, fallback = null) => {
    if (typeof key !== 'string' || !key) {
      return typeof fallback === 'string' ? fallback : '';
    }
    const currentTranslations = translations[language] || translations.en;
    const englishTranslations = translations.en;

    let value = getNestedValue(currentTranslations, key);
    if (value === undefined && language !== DEFAULT_LANGUAGE) {
      value = getNestedValue(englishTranslations, key);
    }
    if (value === undefined) {
      return fallback ?? key;
    }

    if (params && typeof value === 'string') {
      Object.keys(params).forEach((param) => {
        value = value.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
      });
    }

    return value;
  }, [language]);

  // Get effective theme (resolved system to light/dark)
  const getEffectiveTheme = useCallback(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme;
  }, [theme]);

  const value = {
    language,
    setLanguage,
    theme,
    setTheme,
    t,
    isLoading,
    getEffectiveTheme,
    availableLanguages: [
      { code: 'en', name: 'English', nativeName: 'English' },
      { code: 'es', name: 'Spanish', nativeName: 'Español' },
      { code: 'fr', name: 'French', nativeName: 'Français' },
    ],
    availableThemes: [
      { code: 'light', name: 'Light' },
      { code: 'dark', name: 'Dark' },
      { code: 'system', name: 'System' },
    ],
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

/**
 * Hook to use i18n context
 */
export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

/**
 * Hook to get translation function only
 */
export const useTranslation = () => {
  const { t, language } = useI18n();
  return { t, language };
};

export default I18nContext;
